import { useState } from "react";
import { IconButton } from "@/components/common/IconButton";
import { stopChatWorkflowRun } from "@/lib/api";
import {
	isTerminalWorkflowStatus,
	stopResultToast,
	type ChatWorkflowRun,
	type WorkflowRunStatus,
} from "@/lib/chat";
import { useChatStore } from "@/stores/useChatStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useToastStore } from "@/stores/useToastStore";
import { canStopRun } from "./run-stop-permission";

/** ワークフロー実行の停止操作と、その表示可否を担当する。配下のノードにも停止が伝播する。 */
export function WorkflowStopButton({ run }: { run: ChatWorkflowRun }) {
	const chatId = useChatStore((s) => s.chatId);
	const storedStatus = useChatStore(
		(s) => s.workflowRuns.find((entry) => entry.id === run.id)?.status,
	);
	const updateWorkflowRunStatus = useChatStore((s) => s.updateWorkflowRunStatus);
	const currentUserId = useCurrentUserStore((s) => s.id);
	const role = useCurrentUserStore((s) => s.role);
	// 停止要求の結果を保持し、一覧のポーリング (最大 2 秒) を待たずに表示へ反映する。
	const [requestedStatus, setRequestedStatus] = useState<WorkflowRunStatus>();
	const [isStopping, setIsStopping] = useState(false);

	if (chatId === null) return null;
	const latestStatus = storedStatus ?? run.status;
	// 停止要求と正常終了が競合した場合は、先に確定した終端状態を優先する。
	const status = isTerminalWorkflowStatus(latestStatus)
		? latestStatus
		: (requestedStatus ?? latestStatus);
	if (status === "cancelRequested") {
		return (
			<span data-testid="workflow-stop-pending" className="shrink-0 text-caption text-fg-3">
				停止中…
			</span>
		);
	}
	if (isTerminalWorkflowStatus(status)) return null;
	if (!canStopRun(role, currentUserId, run.launchedByUserId)) return null;

	const handleStop = async () => {
		setIsStopping(true);
		try {
			const result = await stopChatWorkflowRun(chatId, run.id);
			setRequestedStatus(result.status);
			updateWorkflowRunStatus(run.id, result.status);
			useToastStore.getState().show(stopResultToast(run.description, result.status));
		} catch (error) {
			useToastStore.getState().show({
				type: "error",
				message: error instanceof Error ? error.message : "ワークフローの停止に失敗しました。",
			});
			throw error;
		} finally {
			setIsStopping(false);
		}
	};

	return (
		<IconButton
			icon="stop"
			size={20}
			iconSize={12}
			color="var(--fg-1)"
			background="var(--surface-2)"
			disabled={isStopping}
			title={isStopping ? "停止を要求しています…" : "ワークフローを停止 (実行中のノードも止まる)"}
			data-testid="workflow-stop"
			// 失敗は handleStop がトーストで伝えたうえで再スローする。ここで受けないと未処理拒否になる。
			onClick={() => void handleStop().catch(() => {})}
		/>
	);
}
